<script setup lang="ts">
import { computed } from "vue";
import Tag from "./shared/tag/TagComponent.vue";
import Title from "./shared/title/TitleComponent.vue";
import Paragraph from "./shared/paragraph/ParagraphComponent.vue";
import Buttons from "./shared/buttons/ButtonsComponent.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import Background from "./shared/background/BackgroundComponent.vue";
import type { WithAppScreenshotProps } from "./hero.interfaces";
import DetailComponent from "./shared/details/DetailComponent.vue";
import DetailsComponent from "./shared/details/DetailsComponent.vue";
import EditableElement from "@/views/page/EditableElement.vue";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import SocialProofComponent from "./shared/socialproof/SocialProofComopnent.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";

const props = defineProps<{
    block: PageBlock<WithAppScreenshotProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <div :id="block.webId">
        <Background :backgroundColor="styleValues.background">
            <FullSpacing>
                <div class="max-w-2xl md:max-w-7xl mx-auto">
                    <div
                        class="flex flex-col items-center justify-center gap-y-8 mx-auto text-center max-w-2xl"
                    >
                        <EditableElement
                            v-if="!dataValues.tag.isHidden"
                            :blockId="block.id"
                            field-name="tag"
                        >
                            <Tag
                                v-bind="dataValues.tag.data"
                                :buttonTextColor="styleValues.brand"
                                :textColor="styleValues.text"
                                :borderColor="styleValues.border"
                            />
                        </EditableElement>
                        <SocialProofComponent
                            :blockId="block.id"
                            :socialProofText="dataValues.socialProofText"
                            :socialProofImages="dataValues.socialProofImages.data"
                            :borderColor="styleValues.background"
                            :textColor="styleValues.text"
                        />
                        <EditableElement
                            v-if="!dataValues.title.isHidden"
                            :blockId="block.id"
                            field-name="title"
                        >
                            <Title v-bind="dataValues.title.data" :textColor="styleValues.text" />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.paragraph.isHidden"
                            :blockId="block.id"
                            field-name="paragraph"
                        >
                            <Paragraph
                                v-bind="dataValues.paragraph.data"
                                :textColor="styleValues.text"
                            />
                        </EditableElement>
                        <DetailsComponent
                            v-if="!dataValues.details.isHidden"
                            :textColor="styleValues.text"
                        >
                            <EditableElement
                                v-for="(detail, index) in dataValues.details.data.items"
                                :key="detail.id"
                                :blockId="block.id"
                                :field-name="`details_${detail.id}`"
                                :hideable="false"
                            >
                                <DetailComponent
                                    :title="detail.title"
                                    :description="detail.description"
                                    :is-last="index === dataValues.details.data.items.length - 1"
                                    :textColor="styleValues.text"
                                />
                            </EditableElement>
                        </DetailsComponent>
                        <Buttons>
                            <EditableElement
                                v-if="!dataValues.primaryButton.isHidden"
                                :blockId="block.id"
                                field-name="primaryButton"
                            >
                                <PrimaryButton
                                    :label="dataValues.primaryButton.data.label"
                                    :href="dataValues.primaryButton.data.href"
                                    :background-color="styleValues.brand"
                                    :text-color="'#ffffff'"
                                    class="px-3.5 py-2.5 rounded-lg text-sm"
                                />
                            </EditableElement>
                            <EditableElement
                                v-if="!dataValues.secondaryButton.isHidden"
                                :blockId="block.id"
                                field-name="secondaryButton"
                            >
                                <SecondaryButton
                                    :label="dataValues.secondaryButton.data.label"
                                    :href="dataValues.secondaryButton.data.href"
                                    :text-color="styleValues.text"
                                    class="text-sm"
                                />
                            </EditableElement>
                        </Buttons>
                    </div>

                    <EditableElement
                        v-if="!dataValues.image.isHidden"
                        :blockId="block.id"
                        fieldName="image"
                    >
                        <div class="relative mt-20">
                            <Image
                                :src="dataValues.image?.data.src"
                                :alt="dataValues.image?.data.alt"
                                class="w-full rounded-t-xl aspect-[16/9] object-cover"
                                width="1280"
                                height="720"
                            />
                            <div
                                class="absolute -bottom-1 left-0 right-0 h-24 sm:h-32 bg-gradient-to-t"
                                :style="{
                                    backgroundImage: `linear-gradient(to top, ${styleValues.background} 0%, transparent 100%)`,
                                }"
                            ></div>
                        </div>
                    </EditableElement>
                </div>
            </FullSpacing>
        </Background>
    </div>
</template>
